import { yupResolver } from '@hookform/resolvers/yup';
import { is400 } from '@src/corelib/utils/httpUtils';
import { isPromise } from '@src/corelib/utils/validationUtils';
import { isArray, isObject, isString, noop } from '@src/utils';
import { useCallback, useRef } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useForm as useFormBase } from 'react-hook-form';

interface YupSchema {
  type: string;
  fields: Record<string, YupSchema>;
  innerType: YupSchema;
}

interface UseFormParams {
  schema?: YupSchema;
  defaultValues?: Record<string, unknown>;
  showServerErrors?: boolean;
  [key: string]: unknown;
}

interface SubmitCallbacks {
  onSubmit?: (values: FieldValues) => unknown;
  onSuccess?: (response: unknown) => void;
  onError?: (error: unknown, info: { isInvalid: boolean }) => void;
  onInvalid?: () => void;
}

function getErrorResponse(error: {
  response: { data: Record<string, unknown> };
}): Record<string, unknown> {
  const responseData = error.response.data;
  return (
    'payload' in responseData ? responseData.payload : responseData
  ) as Record<string, unknown>;
}

function hasKey(key: string, schema: YupSchema): boolean {
  if (schema.type === 'object') {
    return key in schema.fields;
  }

  if (schema.type === 'array') {
    if (schema.innerType.type === 'object') {
      return hasKey(key, schema.innerType);
    }
  }

  return false;
}

function useForm(params: UseFormParams) {
  const {
    schema,
    defaultValues,
    showServerErrors = true,
    ...restParams
  } = params || {};

  const counter = useRef<number>(0);

  const {
    setError,
    handleSubmit: handleSubmitBase,
    setValue,
    reset,
    ...form
  } = useFormBase({
    ...restParams,
    defaultValues,
    resolver: schema
      ? yupResolver(schema as never, { context: { form: { defaultValues } } })
      : undefined,
  });

  const setErrors = useCallback(
    (
      data: Record<string, unknown>,
      nonFieldError: (info: {
        key: string;
        detail: unknown;
      }) => void = () => {},
    ) => {
      counter.current = 0;
      const parseErrors = (
        s: YupSchema,
        d: Record<string, unknown>,
        p = '',
      ) => {
        if (counter.current >= 10000) {
          throw new Error('Unable to parse object.');
        }
        counter.current += 1;

        for (const pair of Object.entries(d)) {
          const [key, value] = pair;
          const errorKey = p ? `${p}.${key}` : key;
          if (isString(value)) {
            if (hasKey(key, s)) {
              setError(errorKey, {
                type: 'invalid',
                message: value,
              });
            } else {
              nonFieldError({ key: errorKey, detail: value });
              break;
            }
          } else if (isArray(value)) {
            if (hasKey(key, s)) {
              value.forEach((item, index) => {
                if (isString(item)) {
                  setError(errorKey, {
                    type: 'invalid',
                    message: item,
                  });
                } else if (isObject(item)) {
                  const _errorKey = `${errorKey}[${index}]`;
                  parseErrors(s.fields[key], item, _errorKey);
                }
              });
            } else {
              nonFieldError({ key: errorKey, detail: value });
              break;
            }
          } else if (isObject(value)) {
            parseErrors(s.fields[key], value, errorKey);
          }
        }
      };

      try {
        if (schema) {
          parseErrors(schema, data);
        }
      } catch (error) {
        console.error('Unable not parse errors.', error);
      }
    },
    [schema, setError],
  );

  const handleSubmit = useCallback(
    ({
      onSubmit = noop,
      onSuccess = noop,
      onError = noop,
      onInvalid = noop,
    }: SubmitCallbacks) => {
      return handleSubmitBase((values) => {
        const promise = onSubmit(values);
        if (isPromise(promise)) {
          return new Promise<void>((resolve) => {
            (promise as Promise<unknown>)
              .then((response: unknown) => {
                onSuccess(response);
                resolve();
              })
              .catch((error: unknown) => {
                const err = error as {
                  response?: {
                    status?: number | string | null;
                    data: Record<string, unknown>;
                  };
                };
                if (is400(err?.response)) {
                  if (showServerErrors) {
                    setErrors(
                      getErrorResponse(
                        err as { response: { data: Record<string, unknown> } },
                      ),
                    );
                  }
                  onError(error, { isInvalid: true });
                } else {
                  console.error('Uexpected form error.', error);
                  onError(error, { isInvalid: false });
                }
                resolve();
              });
          });
        }

        console.log(
          'Non Promise result. Form submitting attributes may not working.',
        );
        return Promise.resolve();
      }, onInvalid);
    },
    [handleSubmitBase, setErrors, showServerErrors],
  );

  return {
    setError,
    setErrors,
    handleSubmitBase,
    handleSubmit,
    reset,
    setValue,
    ...form,
  };
}

export default useForm;
